<template>
  <div class="full-height history-grid-container">
    <q-card class="q-px-sm history-grid-card">
      <q-card-section class="q-pb-lg history-grid-section">
        <jt-data-table
          v-if="datatable"
          ref="jtDataTable"
          class="table-crud"
          :buttons="datatable.buttons"
          :fields="tableFields"
          :forms="forms"
          :extraParams="extraParams"
          :nm-interface="datatableNmInterface"
          singleselect
          @selection="(data) => $emit('selection', data)"
          @click-row="(row) => $emit('click-row', row)"
        >
          <template v-slot:header="{ props }">
            <q-tr :props="props">
              <q-th auto-width />
              <q-th v-for="col in props.cols.filter((item) => item.name !== 'expand')" :key="col.name" :props="props">
                {{ col.label }}
              </q-th>
            </q-tr>
          </template>

          <template v-slot:body="{ props }">
            <q-tr class="history-row" :props="props">
              <q-td auto-width>
                <q-btn
                  flat
                  dense
                  round
                  color="primary"
                  :icon="props.expand ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                  @click="props.expand = !props.expand"
                />
              </q-td>

              <q-td>
                {{ getValue(props, 'tipo') || '-' }}
              </q-td>

              <q-td>
                <div>{{ getDatePart(getValue(props, 'dataEnvio')) || '-' }}</div>
                <div class="history-time">{{ $gettext('HISTORY_TIME_LABEL') }} {{ getTimePart(getValue(props, 'dataEnvio')) || '-' }}</div>
              </q-td>

              <q-td>
                <div class="history-title">{{ getMessageTitle(props) }}</div>
                <div class="history-campaign">{{ getValue(props, 'tituloCampanha') || '-' }}</div>
                <div class="history-summary">{{ getMessageSummary(props) }}</div>
              </q-td>

              <q-td>
                {{ getRecipientContact(props) }}
              </q-td>

              <q-td>
                {{ getUserLabel(props) }}
              </q-td>
            </q-tr>

            <q-tr v-show="props.expand" class="history-detail-row" :props="props">
              <q-td auto-width />

              <q-td class="history-detail-inline">
                <q-icon
                  :name="getChannelIconName(props)"
                  :style="{ color: getChannelIconColor(props) }"
                  size="20px"
                />
                <span>{{ getChannelLabelText(props) }}</span>
              </q-td>

              <q-td class="history-detail-inline">
                <strong>{{ getValue(props, 'status') || '-' }}</strong>
              </q-td>

              <q-td class="history-detail-inline history-detail-inline-message" colspan="3">
                <strong>{{ getMessageTitle(props) }}:</strong>
                <br/>
                <span class="history-detail-inline-message-text">{{ getDetailMessageText(props) }}</span>
              </q-td>
            </q-tr>
          </template>
        </jt-data-table>

        <div v-else-if="loading" class="q-pa-md text-grey-6 text-center">{{ $gettext('HISTORY_LOADING') }}</div>
        <div v-else class="q-pa-md text-negative text-center">
          {{ loadError || $gettext('HISTORY_LOAD_ERROR') }}
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
import { Component, Vue, Watch } from 'vue-property-decorator'
import { JtDataTable } from 'jtech-webcomps'
import { repository } from 'jtech'
import { formatDateTimeLocal } from 'src/shared/utils/dateUtils.js'
import { getChannelColor, getChannelIcon, getChannelLabel } from '../utils/channelMappings.js'

@Component({
  components: { JtDataTable },
  props: {
    consumerUnit: {
      type: Number,
      required: true
    }
  }
})
class CommunicationHistoryGrid extends Vue {
  forms = null
  datatable = null
  loading = false
  loadError = ''

  nmInterface = 'communicationManifest.historico'

  get datatableNmInterface () {
    return this.datatable?.nmInterface || this.nmInterface
  }

  get extraParams () {
    const formData = this.forms?.dadosForm || {}
    return {
      ...formData,
      grid: 'resultado',
      consumerUnit: this.consumerUnit
    }
  }

  get tableFields () {
    return [
      {
        name: 'expand',
        label: '',
        field: 'id',
        align: 'left'
      },
      {
        name: 'tipo',
        label: this.getColumnLabel('channelType_nmDescricao'),
        field: 'channelType_nmDescricao',
        align: 'left'
      },
      {
        name: 'dataEnvio',
        label: this.getColumnLabel('sentAt'),
        field: 'sentAt',
        align: 'left'
      },
      {
        name: 'mensagem',
        label: this.getColumnLabel('subject') || this.getColumnLabel('message'),
        field: 'subject',
        align: 'left'
      },
      {
        name: 'recipient',
        label: this.getColumnLabel('recipient'),
        field: 'recipient',
        align: 'left'
      },
      {
        name: 'user',
        label: this.getColumnLabel('responsible'),
        field: 'createdBy_nmUsuario',
        align: 'left'
      }
    ]
  }

  getColumnLabel (columnName) {
    const columns = this.datatable?.columns || []
    const column = columns.find((item) => {
      return item?.name === columnName || item?.field === columnName || item?.model === columnName
    })
    return column?.label
  }

  extractRootForm (resolved) {
    if (Array.isArray(resolved)) return resolved[0] || null
    if (resolved?.forms && Array.isArray(resolved.forms)) return resolved.forms[0] || null
    return resolved || null
  }

  async created () {
    await this.loadGrid()
  }

  @Watch('consumerUnit')
  async onConsumerUnitChanged () {
    await this.loadGrid()
  }

  async loadGrid () {
    if (!this.consumerUnit) {
      console.error('[CommunicationHistoryGrid] missing prop "consumerUnit"')
      return
    }

    this.loading = true
    this.loadError = ''

    try {
      const resolved = await repository.resolve(this.nmInterface, {
        grid: 'resultado',
        consumerUnit: this.consumerUnit
      })
      const rootForm = this.extractRootForm(resolved)
      const resolvedDatatable = rootForm?.inputs?.find?.((input) => input.type === 'DATATABLE')

      this.forms = rootForm || null
      if (this.forms?.dadosForm) {
        this.forms.dadosForm.consumerUnit = this.consumerUnit
      }
      this.datatable = resolvedDatatable || null

      if (!this.datatable) {
        this.loadError = this.$gettext('HISTORY_DATATABLE_CONFIG_ERROR')
      }
    } catch (error) {
      this.forms = null
      this.datatable = null
      this.loadError = error?.message || this.$gettext('HISTORY_LOAD_FAILURE')
    } finally {
      this.loading = false
    }
  }

  refresh () {
    this.$refs?.jtDataTable?.refresh()
  }

  getValue (props, key) {
    if (!props) return ''
    const aliasMap = {
      tipo: ['tipo', 'channelType_nmDescricao'],
      dataEnvio: ['dataEnvio', 'sentAt'],
      tituloCampanha: ['tituloCampanha', 'campaign_name'],
      assunto: ['assunto', 'subject'],
      status: ['status', 'dispatchStatus_nmDescricao'],
      user: ['user', 'responsible', 'createdBy_nmUsuario', 'createdByName'],
      mensagem: ['message', 'mensagem']
    }

    const keys = aliasMap[key] || [key]

    const fromRowKey = keys.find((currentKey) => {
      const value = props?.row?.[currentKey]
      return value !== undefined && value !== null && value !== ''
    })

    const fromRow = fromRowKey ? props?.row?.[fromRowKey] : undefined
    if (fromRow !== undefined && fromRow !== null && fromRow !== '') return fromRow

    const col = (props.cols || []).find(item => keys.includes(item.name))
    return col?.value || ''
  }

  getDatePart (value) {
    if (!value) return ''
    const formatted = formatDateTimeLocal(value)
    if (formatted) {
      return formatted.split(' ')[0]
    }
    return ''
  }

  getTimePart (value) {
    if (!value) return ''
    const formatted = formatDateTimeLocal(value)
    if (formatted) {
      const timePart = formatted.split(' ')[1]?.trim() || ''
      const [hours, minutes] = timePart.split(':')
      return `${hours}:${minutes}`
    }
    return ''
  }

  truncate (value, size = 90) {
    if (!value) return '-'
    const text = String(value)
    return text.length > size ? `${text.slice(0, size)}...` : text
  }

  sanitizeMessageHtml (value) {
    if (!value) return ''

    return String(value)
      .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
      .replace(/<li>(?:\s|&nbsp;|<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)*<\/li>/gi, '')
      .replace(/<ul>(?:\s|&nbsp;)*<\/ul>/gi, '')
      .trim()
  }

  getMessageContent (props) {
    return this.sanitizeMessageHtml(
      this.getValue(props, 'message') || this.getValue(props, 'mensagem') || ''
    )
  }

  getChannelType (props) {
    const value = this.getValue(props, 'channelType')
    if (value === undefined || value === null || value === '') return null
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }

  stripHtml (value) {
    if (!value) return ''
    return String(value)
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<li>/gi, '- ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+\n/g, '\n')
      .replace(/\n\s+/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s{2,}/g, ' ')
      .trim()
  }

  toSingleLineText (value) {
    if (!value) return ''
    return this.stripHtml(value)
      .replace(/\s+/g, ' ')
      .trim()
  }

  escapeHtml (value) {
    if (!value) return ''
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  getMessageTitle (props) {
    return this.getValue(props, 'assunto') || this.getValue(props, 'tituloCampanha') || '-'
  }

  getMessageSummary (props) {
    return this.truncate(this.toSingleLineText(this.getMessageContent(props)), 140)
  }

  getRecipientContact (props) {
    return this.getValue(props, 'recipient') || '-'
  }

  getUserLabel (props) {
    return this.getValue(props, 'user') || '-'
  }

  getChannelIconName (props) {
    return getChannelIcon(this.getChannelType(props))
  }

  getChannelIconColor (props) {
    return getChannelColor(this.getChannelType(props))
  }

  getChannelLabelText (props) {
    return getChannelLabel(this.getChannelType(props)) || this.getValue(props, 'tipo') || '-'
  }

  getDetailMessageText (props) {
    const message = this.getMessageContent(props)
    if (message) return this.stripHtml(message)

    const error = this.getValue(props, 'erro')
    if (error) return this.stripHtml(error)

    return '-'
  }
}

export default CommunicationHistoryGrid
</script>

<style lang="stylus" scoped>
.history-grid-container
  width: 100%
  height: 100%
  min-height: 0
  overflow: hidden

.history-grid-card
  width: 100%
  height: 100%
  max-height: 100%
  display: flex
  flex-direction: column
  overflow: hidden

.history-grid-section
  flex: 1
  min-height: 0
  overflow: hidden

.history-row
  .history-title
    font-weight: 600

  .history-campaign
    color: #374151
    font-size: 12px

  .history-summary
    color: #5f6368
    font-size: 12px
    margin-top: 4px
    text-align: left

  .history-time
    color: #5f6368
    font-size: 12px

.history-detail-row
  background-color: #f8fafc

.history-detail-inline
  color: #374151
  text-align: left
  vertical-align: middle

.history-detail-inline-channel
  display: flex
  align-items: center
  gap: 8px
  font-weight: 600

.history-detail-inline-message
  color: #1f2937
  white-space: normal
  overflow-wrap: anywhere
  word-break: break-word
  line-height: 1.35

.history-detail-inline-message-text
  margin-left: 4px
  white-space: pre-wrap

.history-detail-inline-separator
  margin: 0 8px
  color: #9ca3af

::v-deep .table-crud
  width: 100%
  height: 100%
  max-width: 100%
  max-height: 100%
  overflow: hidden

::v-deep .table-crud .jt-datatable
  width: 100%
  height: 100% !important
  max-height: 100% !important

::v-deep .table-crud .q-table__container
  width: 100%
  max-width: 100%
  height: 100%
  max-height: 100%

::v-deep .table-crud .q-table__middle
  width: 100%
  max-width: 100%
  max-height: 100%
  overflow: auto

::v-deep .table-crud table
  width: 100%
  max-width: 100%
  table-layout: fixed

::v-deep .table-crud th:first-child,
::v-deep .table-crud td:first-child
  width: 52px
  min-width: 52px
  max-width: 52px
  overflow: visible

::v-deep .table-crud td
  vertical-align: middle

::v-deep .history-detail-message p
  margin: 0 !important

::v-deep .history-detail-message ul
  margin: 0 0 0 18px !important

::v-deep .history-detail-message li
  margin-bottom: 0 !important

::v-deep .history-detail-message pre
  white-space: pre-wrap
  margin: 0 !important
  font-family: inherit

.history-detail-status
  color: #374151
</style>
